import React, { useContext, useEffect, useState } from 'react'
import axios from '../../../../config/axios'
import { Pencil, Trash2 } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Filter } from '../../../../components/SubHeaderFilter/types'
import { StandardDataTable, useColumnsState } from '../../../../components/StandardPage'
import ApiService from '../../../../services/ApiService'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import { TableContainer } from '../style'
import {
  buildCompaniesColumns,
  companiesScreenKey,
  CompanyPersonType,
  CompanyRow,
} from './companiesColumns'

interface TableCompanyType {
  appliedFilters?: Filter[]
  personType?: CompanyPersonType
  search?: string
}

const TableCompanies: React.FC<TableCompanyType> = ({ appliedFilters, personType = null, search }) => {
  const [companies, setCompanies] = useState<CompanyRow[]>([])
  const [totalCompanies, setTotalCompanies] = useState<number | undefined>(undefined)
  const [lastFetchedCount, setLastFetchedCount] = useState<number>(0)
  const [quantity, setQuantity] = useState<number>(50)
  const [deleteTarget, setDeleteTarget] = useState<CompanyRow | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setFeedback } = useContext(FeedbackContext)

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    try {
      await ApiService.companies.delete(Number(deleteTarget.id))
      setDeleteTarget(null)
      queryClient.invalidateQueries({ queryKey: ['companies'] })
      setFeedback({ isOpen: true, message: 'Empresa excluída com sucesso!', statusOk: true })
    } catch (error: any) {
      console.error('Erro ao excluir empresa:', error)
      const msg = error?.response?.data?.message || error?.response?.data?.error || 'Não foi possível excluir a empresa. Ela pode possuir relações com outros registros.'
      setDeleteTarget(null)
      setFeedback({ isOpen: true, message: msg, statusOk: false })
    }
  }

  const columns = React.useMemo(() => buildCompaniesColumns(personType), [personType])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(companiesScreenKey, columns)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['companies', appliedFilters, quantity, search, personType],
    queryFn: async () => {
      const params: any = {
        limit: quantity,
        offset: 0,
      }
      if (personType === 'Própria') {
        params.isMain = 'true'
      } else {
        params.excludeMainCompany = 'true'
      }

      if (personType === 'Transportadora') {
        params.companyType = 'Transportadora'
      }

      if (search && search.trim()) {
        params.query = search.trim()
      }
      if (personType === 'Jurídica') {
        params.personType = 'juridica'
      } else if (personType === 'Física') {
        params.personType = 'fisica'
      }
      if (appliedFilters && Array.isArray(appliedFilters) && appliedFilters.length > 0) {
        params.filters = JSON.stringify(appliedFilters)
      }

      const resp = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })

      let mainCompanies: CompanyRow[] = []
      if (personType === null) {
        const mainParams = { ...params }
        delete mainParams.excludeMainCompany
        mainParams.isMain = 'true'
        try {
          const mainResp = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`, {
            params: mainParams,
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          })
          mainCompanies = Array.isArray(mainResp?.data) ? mainResp.data : []
        } catch (err) {
          mainCompanies = []
        }
      }

      let countValue: number | undefined = undefined

      const countResp = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/count`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })

      try {
        const headerTotal = countResp?.headers?.['x-total-count'] ?? countResp?.headers?.['X-Total-Count']
        const candidateValues = [countResp?.data, countResp?.data?.totalItems, countResp?.data?.total, headerTotal]
        for (const v of candidateValues) {
          const n = Number(v)
          if (Number.isFinite(n)) { countValue = n; break }
        }
      } catch (err) {
        countValue = undefined
      }

      try {
        const listLength = Array.isArray(resp?.data) ? resp.data.length : undefined
        if (typeof countValue === 'number') {
          countValue += mainCompanies.length
        }
        if (typeof countValue === 'number' && typeof listLength === 'number' && listLength + mainCompanies.length > countValue) {
          setTotalCompanies(listLength + mainCompanies.length)
        } else {
          setTotalCompanies(countValue)
        }
      } catch (err) {
        setTotalCompanies(undefined)
      }

      const regularCompanies: CompanyRow[] = Array.isArray(resp?.data) ? resp.data : []
      const mainCompanyIds = new Set(mainCompanies.map((company) => company.id))
      return [...mainCompanies, ...regularCompanies.filter((company) => !mainCompanyIds.has(company.id))]
    }
  })

  useEffect(() => {
    if (data) {
      const original = Array.isArray(data) ? data : []
      setLastFetchedCount(original.length)
      setCompanies(original)
    } else if (isError) {
      setCompanies([])
    }
  }, [data, isError])

  useEffect(() => {
    setQuantity(50)
  }, [personType, appliedFilters, search])

  const hasMore = typeof totalCompanies === 'number' ? companies.length < totalCompanies : lastFetchedCount >= quantity

  return (
    <TableContainer>
      <StandardDataTable
        columns={columns}
        data={companies}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={typeof totalCompanies === 'number' ? totalCompanies : null}
        onLoadMore={() => setQuantity((prev) => prev + 50)}
        hasMore={hasMore}
        isLoading={isLoading}
        countLabel='empresas'
        exportFileName='empresas'
        emptyMessage='Nenhuma empresa encontrada.'
        rowActions={(company) => [
          { label: 'Editar', icon: <Pencil size={16} />, onClick: () => navigate('edit/?id=' + company.id) },
          { label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => setDeleteTarget(company) },
        ]}
      />

      <ConfirmationModal
        isOpen={!!deleteTarget}
        title="Excluir Empresa"
        message="Tem certeza que deseja excluir esta empresa? Esta ação não poderá ser desfeita."
        actions={[
          { label: 'Cancelar', onClick: () => setDeleteTarget(null), variant: 'outline' },
          { label: 'Excluir', onClick: handleDeleteConfirm, variant: 'danger' },
        ]}
      />
    </TableContainer>
  )
}

export default TableCompanies
